import React from 'react';
import { Box, Paper, Typography, Button, IconButton, Tooltip, Dialog, DialogTitle, DialogContent, DialogActions } from '@mui/material';
import PrintIcon from '@mui/icons-material/Print';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import { BACKGROUND_CHECK_TEXT, PSP_TEXT } from './authorizationTexts';
import Table from '@mui/material/Table';
import TableBody from '@mui/material/TableBody';
import TableCell from '@mui/material/TableCell';
import TableContainer from '@mui/material/TableContainer';
import TableHead from '@mui/material/TableHead';
import TableRow from '@mui/material/TableRow';

function formatDate(value) {
    if (!value) return '—';
    try {
        const d = typeof value === 'string' ? new Date(value) : value;
        return d.toLocaleString();
    } catch (_) {
        return String(value);
    }
}

/** Normalize stored PDF payload to raw base64 (handles full data-URL or bare base64). */
function normalizePdfBase64(input) {
    if (!input || typeof input !== 'string') return null;
    const trimmed = input.trim();
    if (trimmed.startsWith('data:')) {
        const comma = trimmed.indexOf(',');
        return comma >= 0 ? trimmed.slice(comma + 1) : null;
    }
    return trimmed;
}

function pdfBase64ToBlob(input) {
    const raw = normalizePdfBase64(input);
    if (!raw) return null;
    try {
        const binary = atob(raw);
        const bytes = new Uint8Array(binary.length);
        for (let i = 0; i < binary.length; i++) bytes[i] = binary.charCodeAt(i);
        return new Blob([bytes], { type: 'application/pdf' });
    } catch (_) {
        return null;
    }
}

function createPdfObjectUrl(input) {
    if (typeof input === 'string' && /^https?:\/\//i.test(input)) return input;
    const blob = pdfBase64ToBlob(input);
    if (!blob) return null;
    return URL.createObjectURL(blob);
}

function printHtmlViaIframe(html) {
    try {
        const iframe = document.createElement('iframe');
        iframe.style.position = 'fixed';
        iframe.style.right = '0';
        iframe.style.bottom = '0';
        iframe.style.width = '0';
        iframe.style.height = '0';
        iframe.style.border = '0';
        document.body.appendChild(iframe);
        const doc = iframe.contentWindow?.document;
        if (!doc) return;
        doc.open();
        doc.write(`<!doctype html><html><head><meta charset="utf-8" /><meta name="viewport" content="width=device-width, initial-scale=1" /><title>Print</title>
          <style>
            @page { size: auto; margin: 10mm; }
            html, body { height: auto; }
            body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, Oxygen, Ubuntu, Cantarell, sans-serif; color: #111827; margin: 0; }
            .doc { max-width: 7.5in; margin: 0 auto; padding: 10mm; }
            table { width: 100%; border-collapse: collapse; }
            th, td { border: 1px solid #e5e7eb; padding: 8px 10px; text-align: left; font-size: 14px; }
            th { background: #f8fafc; font-weight: 700; }
            h1 { font-size: 18px; margin: 0 0 8px; }
            .sub { font-size: 12px; color: #6b7280; margin: 0 0 8px; }
            img { max-height: 120px; border: 1px solid #e5e7eb; border-radius: 6px; }
            .section { margin: 8px 0; }
            .body { font-size: 12px; line-height: 1.35; white-space: normal; word-break: break-word; overflow-wrap: anywhere; }
            .body p { margin: 0 0 6px; }
          </style>
        </head><body><div class="doc">${html}</div></body></html>`);
        doc.close();
        setTimeout(() => {
            iframe.contentWindow?.focus();
            iframe.contentWindow?.print();
            setTimeout(() => document.body.removeChild(iframe), 500);
        }, 250);
    } catch (_) {}
}

function ViewerDialog({ open, onClose, html, onPrint, pdfUrl }) {
    // PDF path: blob: or https URL in iframe (CSP allows blob:/data: in frame-src)
    if (pdfUrl) {
        return (
            <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
                <DialogTitle>PSP Disclosure and Authorization Form</DialogTitle>
                <DialogContent dividers sx={{ p: 0, height: '80vh' }}>
                    <Box sx={{ width: '100%', height: '100%' }}>
                        <iframe
                            src={pdfUrl}
                            style={{
                                width: '100%',
                                height: '100%',
                                border: 'none'
                            }}
                            title="PSP Disclosure PDF"
                        />
                    </Box>
                </DialogContent>
                <DialogActions>
                    <Button
                        onClick={() => {
                            const printWindow = window.open(pdfUrl, '_blank');
                            if (printWindow) {
                                printWindow.onload = () => {
                                    printWindow.print();
                                };
                            }
                        }}
                        startIcon={<PrintIcon />}
                    >
                        Print
                    </Button>
                    <Button
                        onClick={() => {
                            const link = document.createElement('a');
                            link.href = pdfUrl;
                            link.download = 'PSP-Disclosure-and-Authorization-Form_Actual.pdf';
                            document.body.appendChild(link);
                            link.click();
                            document.body.removeChild(link);
                        }}
                    >
                        Download
                    </Button>
                    <Button onClick={onClose}>Close</Button>
                </DialogActions>
            </Dialog>
        );
    }

    // Fallback to HTML view for text-based documents
    return (
        <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
            <DialogTitle>Authorization Document</DialogTitle>
            <DialogContent dividers>
                <Box sx={{ fontFamily: 'inherit' }}
                    dangerouslySetInnerHTML={{ __html: `
                        <div class=\"doc\" style=\"max-width:800px;margin:0 auto;\">
                          ${html}
                        </div>
                    ` }}
                />
            </DialogContent>
            <DialogActions>
                <Button onClick={onPrint}>Print</Button>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}

function buildFullDocHtml(docTitle, bodyText, row, applicant) {
    const applicantName = [applicant?.firstName, applicant?.lastName].filter(Boolean).join(' ') || '—';
    const bodyHtml = buildBodyHtml(bodyText || '');
    return `
      <h1 class="title">${docTitle}</h1>
      <p class="sub">Applicant: ${applicantName}</p>
      <div class="section body">${bodyHtml}</div>
      <hr class="hr" />
      <div class="section">
        <div class="label">Signature</div>
        ${row.signature ? `<img src="${row.signature}" alt="Signature" />` : '<div class="value">No signature on file</div>'}
      </div>
      <div class="section" style="display:grid; grid-template-columns:1fr 1fr; gap:12px;">
        <div>
          <div class="label">Typed Name</div>
          <div class="value">${row.typed || '—'}</div>
        </div>
        <div>
          <div class="label">Signed At</div>
          <div class="value">${row.signedAt || '—'}</div>
        </div>
      </div>
    `;
}

function escapeHtml(s) {
    return String(s)
        .replace(/&/g, '&amp;')
        .replace(/</g, '&lt;')
        .replace(/>/g, '&gt;');
}

function buildBodyHtml(text) {
    try {
        const normalized = text.replace(/\r\n/g, '\n').trim();
        const paragraphs = normalized.split(/\n\s*\n+/); // split on blank lines
        const htmlParas = paragraphs.map(p => {
            const oneLine = p.replace(/\n+/g, ' ').replace(/\s{2,}/g, ' ').trim();
            return `<p>${escapeHtml(oneLine)}</p>`;
        });
        return htmlParas.join('');
    } catch (_) {
        return `<p>${escapeHtml(text)}</p>`;
    }
}

export default function AuthorizationsTable({ backgroundEsign, pspEsign, applicant, sourcePayload }) {
    const [viewerOpen, setViewerOpen] = React.useState(false);
    const [viewerHtml, setViewerHtml] = React.useState('');
    const [viewerPdfUrl, setViewerPdfUrl] = React.useState(null);
    const blobUrlRef = React.useRef(null);

    const revokeBlobUrl = React.useCallback(() => {
        if (blobUrlRef.current) {
            URL.revokeObjectURL(blobUrlRef.current);
            blobUrlRef.current = null;
        }
    }, []);

    React.useEffect(() => () => revokeBlobUrl(), [revokeBlobUrl]);

    // Check if PSP PDF is available from ktlanding submission
    const pspPdfBase64 = pspEsign?.pdf_base64
        || sourcePayload?.authorizations_esign?.psp?.pdf_base64
        || sourcePayload?.pdf_base64
        || sourcePayload?.meta?.pdf_base64
        || null;

    const rows = [
        {
            name: 'Background Check Authorization',
            typed: backgroundEsign?.typed_name || '—',
            signedAt: formatDate(backgroundEsign?.signed_at_iso || null),
            signature: backgroundEsign?.signature_data_url || '',
            title: 'Background Check Authorization',
            fullText: BACKGROUND_CHECK_TEXT,
            hasPdf: false
        },
        {
            name: 'PSP Authorization',
            typed: pspEsign?.typed_name || '—',
            signedAt: formatDate(pspEsign?.signed_at_iso || null),
            signature: pspEsign?.signature_data_url || '',
            title: 'PSP (Pre-Employment Screening Program) Authorization',
            fullText: PSP_TEXT,
            hasPdf: !!pspPdfBase64,
            pdfBase64: pspPdfBase64
        }
    ];

    const openPdfBlob = (pdfBase64) => {
        if (typeof pdfBase64 === 'string' && /^https?:\/\//i.test(pdfBase64)) {
            return pdfBase64;
        }
        revokeBlobUrl();
        const url = createPdfObjectUrl(pdfBase64);
        if (!url) return null;
        blobUrlRef.current = url;
        return url;
    };

    const handleRowPrint = (row) => {
        if (row.hasPdf && row.pdfBase64) {
            const url = openPdfBlob(row.pdfBase64);
            if (!url) return;
            const printWindow = window.open(url, '_blank');
            if (printWindow) {
                printWindow.onload = () => {
                    printWindow.print();
                };
            }
        } else {
            const html = buildFullDocHtml(row.title, row.fullText, row, applicant);
            printHtmlViaIframe(html);
        }
    };

    const handleRowView = (row) => {
        if (row.hasPdf && row.pdfBase64) {
            const url = openPdfBlob(row.pdfBase64);
            setViewerPdfUrl(url);
            setViewerHtml('');
            setViewerOpen(true);
        } else {
            const html = buildFullDocHtml(row.title, row.fullText, row, applicant);
            setViewerHtml(html);
            setViewerPdfUrl(null);
            setViewerOpen(true);
        }
    };

    const handleCloseViewer = () => {
        setViewerOpen(false);
        setViewerPdfUrl(null);
        setViewerHtml('');
        revokeBlobUrl();
    };

    return (
        <Paper variant="outlined" sx={{ p: 2 }}>
            <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="subtitle1" sx={{ fontWeight: 700 }}>Authorizations</Typography>
            </Box>
            <TableContainer>
                <Table size="small" aria-label="authorizations table">
                    <TableHead>
                        <TableRow>
                            <TableCell>Document</TableCell>
                            <TableCell>Typed Name</TableCell>
                            <TableCell>Signed At</TableCell>
                            <TableCell>Signature</TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {rows.map((r) => (
                            <TableRow key={r.name}>
                                <TableCell>{r.name}</TableCell>
                                <TableCell>{r.typed}</TableCell>
                                <TableCell>{r.signedAt}</TableCell>
                                <TableCell>
                                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                        {r.hasPdf && (
                                            <Tooltip title="View PDF">
                                                <IconButton size="small" onClick={() => handleRowView(r)} color="primary">
                                                    <PictureAsPdfIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        )}
                                        <Button size="small" onClick={() => handleRowView(r)}>
                                            {r.hasPdf ? 'View PDF' : 'View'}
                                        </Button>
                                        <Tooltip title="Print">
                                            <IconButton size="small" onClick={() => handleRowPrint(r)}>
                                                <PrintIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </Box>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>
            <ViewerDialog
                open={viewerOpen}
                onClose={handleCloseViewer}
                html={viewerHtml}
                onPrint={() => {
                    if (viewerPdfUrl) {
                        const printWindow = window.open(viewerPdfUrl, '_blank');
                        if (printWindow) {
                            printWindow.onload = () => {
                                printWindow.print();
                            };
                        }
                    } else {
                        printHtmlViaIframe(viewerHtml);
                    }
                }}
                pdfUrl={viewerPdfUrl}
            />
        </Paper>
    );
}
